// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.ReportPartChartContent.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

/**
 * ReportPartChartContent model
 */
import { observable, action, computed, extendObservable, reaction, toJS, isObservableArray, isObservable } from 'mobx';
import tracker from 'share/tracking/Izenda.Tracker';
import { EXPANDED_LEVEL, DATA_TYPES, REPORT_PART_TYPE } from 'logic/data/Izenda.ReportDesignerConstants';
import { exportClass } from 'share/Izenda.ServiceProvider';
import ReportPartContent from './Izenda.ReportPartContent';
import ReportPartFieldConstants from 'logic/data/Izenda.ReportPartFieldConstants';
import ReportBodyConstants from 'logic/data/Izenda.ReportBodyConstants';
import ChartDefaultConfig from 'logic/data/Izenda.ChartDefaultConfig';
import CONSTANTS from 'logic/data/Izenda.ReportPartChartConstants';
import resolveAvailableFunction from 'logic/data/Izenda.ChartAvailableFunction';
import ReportUtils from './Izenda.ReportUtils';
import ReportPartUtils from 'share/utils/Izenda.ReportPartUtils';
import { Highcharts as DefaultVizEngine } from 'share/components/charting/highcharts/Izenda.HighchartsVizNames';

import {
  slice,
  placeholder,
  get,
  flatMap,
  compact,
  remove,
  isUndefined,
  getOr,
  assign,
  isArray,
  isObject,
  omitBy,
  omit,
  map,
  find,
  some,
  includes,
  flow,
  head,
  nth,
  isNil,
  first,
  size,
  findIndex,
  isEmpty,
  last,
  merge,
  isFunction,
  filter,
  forEach,
  has,
  reduce
} from 'lodash/fp';

const SORT_ORDERS = ReportPartFieldConstants.SORT_ORDERS;
const {
  ContainerName,
  COMBINE_CHART: { DEFAULT_VALUE: COMBINE_CHART_DEFAULT_VALUE },
  SPARK_LINE_CHART: { DEFAULT_VALUE: SPARKLINE_CHART_DEFAULT_VALUE }
} = CONSTANTS;
const {
  CHART_TYPE,
  DefaultConfig: { METRIC_ALL }
} = ChartDefaultConfig;

const NEED_UPDATE_DATA_CHART_TYPE = [
  CHART_TYPE.Bubble,
  CHART_TYPE.Scatter,
  CHART_TYPE.Donut,
  CHART_TYPE.HeatMap,
  CHART_TYPE.Sparkline,
  CHART_TYPE.TreeMap
];
const {
  populateReportChartCustomContainer,
  populateReportChartContainer,
  getElements,
  hasElement,
  resetDefaultSetting
} = ReportPartUtils;

const NUMBER_ZERO = 0;
const NUMBER_ONE = 1;
const NUMBER_TWO = 2;
const NUMBER_THREE = 3;
const NUMBER_FIVE = 5;
const NUMBER_CHECK_INDEX = -1;

@exportClass('ReportPartChartContent')
@tracker
class ReportPartChartContent extends ReportPartContent {
  @observable isCrossFiltering = false;
  @observable title = null;
  @observable description = null;
  // use to show/hide fields which need to draw chart - do not save
  @observable activeSerie = null;
  @observable inconsitentFunction = false;
  multiColorDirty = false;

  @observable
  properties = {
    chartType: CONSTANTS.DefaultChartType,
    vizEngine: DefaultVizEngine,
    colorTheme: undefined,
    isDefaultTheme: true,
    commonOptions: {
      // 'izLegend.visibility': false
      izDisableMultiColor: false
    },
    optionByType: {
      izTotalLabel: '',
      izMultiColor: false,
      izMultiLevel: false
    },
    view: {
      dataRefreshInterval: {
        enable: false,
        updateInterval: 0,
        isAll: true,
        latestRecord: 0
      }
    },
    commonXYAxis: {},
    printing: {
      izPageBreakAfterSeparator: false
    }
  };

  @observable
  drilldownInfo = {
    separatorValue: null,
    filters: []
  };

  // container to store data when user changes chart type.
  containersHistory = [];
  containersHistoryMetric = [];

  constructor(reportPartContent, containersHistory, containersHistoryMetric) {
    super(REPORT_PART_TYPE.Chart);

    // properties which do not to observer
    this.staticProperties = {};

    const props = {};

    this.containersHistory = containersHistory || [];
    this.containersHistoryMetric = containersHistoryMetric || [];

    // important - these containers save list of fields to get data and render report
    ReportPartUtils.addContainers(props, ContainerName.LABELS);
    ReportPartUtils.addContainers(props, ContainerName.VALUES);
    ReportPartUtils.addContainers(props, ContainerName.VALUES_LABELS);
    ReportPartUtils.addContainers(props, ContainerName.SEPARATORS);
    ReportPartUtils.addContainers(props, ContainerName.BUBBLE_SIZE);

    this.addCustomContainer(ContainerName.DYNAMIC_THRESHOLD);

    extendObservable(this, props);

    !!reportPartContent ? this.readData(reportPartContent) : this.setReportPartProperties();

    // trick - access num elements of each container to trigger change
    // every have action on component (add/delete field, visible/hidden element)
    this.drilldownReaction = reaction(
      () =>
        this[ContainerName.LABELS].numElement +
        this[ContainerName.VALUES].numElement +
        this[ContainerName.VALUES_LABELS].numElement +
        this[ContainerName.SEPARATORS].numElement +
        this[ContainerName.BUBBLE_SIZE].numElement,
      this.resetDrilldownInfo
    );

    this.cleanDirty();
  }

  @computed
  get chartType() {
    return this.properties.chartType;
  }

  @computed
  get expandedLevel() {
    return this.isChartType(this.chartType, CHART_TYPE.Donut) || this.treeMapWithMultiLevel
      ? EXPANDED_LEVEL.ALL
      : this.drilldownInfo.filters.length;
  }

  @computed
  get treeMapWithMultiLevel() {
    const izMultiLevel = get('properties.optionByType.izMultiLevel', this);
    return this.isChartType(this.chartType, CHART_TYPE.TreeMap) && izMultiLevel;
  }

  @computed
  get defaultExpandedLevel() {
    return this.isChartType(this.chartType, CHART_TYPE.Donut)
      ? EXPANDED_LEVEL.ALL
      : EXPANDED_LEVEL[this.hasDrillDown ? 'FIRST_LEVEL' : 'ALL'];
  }

  @computed
  get drilldown() {
    return this.getDrilldownInfo();
  }

  @computed
  get isDisableMultiColor() {
    return (
      this.numberOfMetric > NUMBER_ONE &&
      !this.isChartType(this.chartType, CHART_TYPE.Donut, CHART_TYPE.Pie, CHART_TYPE.Funnel, CHART_TYPE.TreeMap)
    );
  }

  @computed
  get numberOfMetric() {
    return flow(
      get('serieChartList'),
      size
    )(this);
  }

  @computed
  get serieChartList() {
    let metricContainer = this.getMetricContainer();
    metricContainer = this[metricContainer] && this[metricContainer].visibleElement;

    return !metricContainer ? [] : metricContainer.map(elm => elm);
  }

  @computed
  get activeElmsByContainers() {
    return this.getActiveElmsByActiveSeries(get('fieldName', this.activeSerie));
  }

  @computed
  get allFieldCollection() {
    return flow(
      flatMap(i => (i.elements ? i.elements.slice() : [])),
      filter(i => !!i)
    )(this.containerCollection);
  }

  @computed
  get containerCollection() {
    const collection = [this.labels, this.values, this.valuesLabels, this.separators, this.bubbleSize];

    this.customContainers.forEach(customContainer => {
      collection.push(customContainer);
    });
    return collection;
  }

  @computed
  get hasDrillDown() {
    return (
      !!(this.labels && this.labels.visibleElement && this.labels.visibleElement.length > NUMBER_ONE) &&
      !this.isChartType(this.chartType, CHART_TYPE.Donut)
    );
  }

  @computed
  get labelsElements() {
    return this.labels.visibleElement;
  }

  @computed
  get valuesElements() {
    return this.values.visibleElement;
  }

  @computed
  get separatorsElements() {
    return this.separators.visibleElement;
  }

  @computed
  get valuesLabelsElements() {
    return this.valuesLabels.visibleElement;
  }

  @computed
  get bubbleSizeElements() {
    return this.bubbleSize.visibleElement;
  }

  @computed
  get isBeingBuild() {
    if (this.inconsitentFunction) {
      return false;
    }
    switch (true) {
      case this.isChartType(
        this.chartType,
        CHART_TYPE.Line,
        CHART_TYPE.Column,
        CHART_TYPE.Bar,
        CHART_TYPE.Area,
        CHART_TYPE.Pie,
        CHART_TYPE.Funnel,
        CHART_TYPE.Donut,
        CHART_TYPE.Combination,
        CHART_TYPE.TreeMap,
        CHART_TYPE.Waterfall
      ):
        return hasElement(this.labels, this.values);
      case this.isChartType(this.chartType, CHART_TYPE.HeatMap, CHART_TYPE.Bubble, CHART_TYPE.Scatter):
        return hasElement(this.labels, this.values, this.valuesLabels);

      case this.isChartType(this.chartType, CHART_TYPE.Sparkline):
        return hasElement(this.values, this.labels);

      default:
        return false;
    }
  }

  @computed
  get listCountableFieldInLabelContainer() {
    return ReportUtils.getCountableElements(this.labelsElements);
  }

  @computed
  get listLinearFieldInLabelContainer() {
    return remove('properties.otherProps.axis.izCategory', this.listCountableFieldInLabelContainer);
  }

  @computed
  get listCountableFieldInValueContainer() {
    return ReportUtils.getCountableElements(this.valuesElements);
  }

  @computed
  get dataRefreshInterval() {
    return get('view.dataRefreshInterval', this.properties);
  }

  @computed
  get optionalParams() {
    return {
      expandedLevel: this.defaultExpandedLevel
    };
  }

  @computed
  get isShowMetric() {
    return this.isChartType(this.chartType, CHART_TYPE.HeatMap, CHART_TYPE.Waterfall)
      ? false
      : !isEmpty(this.serieChartList) && this.serieChartList.length > NUMBER_ONE;
  }

  @computed
  get isShowBreadcrumb() {
    const unsupportedBreadcrumbChartTypes = [
      CHART_TYPE.Donut,
      CHART_TYPE.HeatMap,
      CHART_TYPE.Bubble,
      CHART_TYPE.Scatter,
      CHART_TYPE.Waterfall,
      CHART_TYPE.Sparkline
    ];
    return !(this.isChartType(this.chartType, ...unsupportedBreadcrumbChartTypes) || this.treeMapWithMultiLevel);
  }

  @computed
  get reportPartStyle() {
    return this.properties.chartType;
  }

  @computed
  get vizEngine() {
    return this.properties.vizEngine;
  }

  get defaultVizEngine() {
    return DefaultVizEngine;
  }

  @action
  setVizEngine(value: string) {
    this.properties.vizEngine = value;
  }

  @computed
  get currentDrilldownField() {
    const index = this.expandedLevel;
    return get(`labelsElements[${index}]`, this);
  }

  /**
   * Determine whether the report part content automatically sizes its container or not
   */
  get autoSize() {
    return false;
  }

  /**
   * check if function is compatible with element
   * @param {Object} container
   * @param {Object} element
   * @param {Object} functionInfo
   * @return {boolean}
   */
  checkedCompatibleFunction(container: any, element: any, functionInfo: any): boolean {
    if (!container) {
      return true;
    }

    let result = true;
    const functionName = getOr(ReportPartFieldConstants.EMPTY_FUNCTION.name, 'name', functionInfo);
    const hasAggregatedFunction = element.field.hasAggregatedFunction;

    // If the field is in Values container of Column/Bar/Area chart type with selected Range Only,
    // then no error message is shown. Behind the scene, system will get the Min and Max function to draw those charts
    // function will not have any meaning in this case, so we will treat all function as invalid
    if (
      this.isChartType(this.chartType, CHART_TYPE.Column, CHART_TYPE.Area, CHART_TYPE.Bar) &&
      container.name === 'values' &&
      get('optionByType.izRange', this.properties)
    ) {
      return false;
    }

    // If the user add a calculated field which contains the aggregated field
    // then the user cannot select any aggregated field in Function dropdown in
    // Field Properties Panel. The user can select “empty” function or other user
    // defined functions which has function level = field.
    if (hasAggregatedFunction) {
      result = functionInfo.userDefined || !functionName;
    }

    const checkDataTypeNumericMoney = includes(placeholder, ['Numeric', 'Money']);

    const needNumericMoneyChartTypes = [
      CHART_TYPE.Line,
      CHART_TYPE.Column,
      CHART_TYPE.Bar,
      CHART_TYPE.Area,
      CHART_TYPE.Pie,
      CHART_TYPE.Funnel,
      CHART_TYPE.Donut,
      CHART_TYPE.Combination,
      CHART_TYPE.TreeMap,
      CHART_TYPE.Waterfall,
      CHART_TYPE.Sparkline
    ];

    if (
      result &&
      ((this.isChartType(this.chartType, CHART_TYPE.Bubble) && container.name === 'bubbleSize') ||
        (this.isChartType(this.chartType, ...needNumericMoneyChartTypes) && container.name === 'values'))
    ) {
      // function return Money or Numeric
      result =
        checkDataTypeNumericMoney(functionInfo.formatDataType) ||
        // Element data type is Money or numeric and function is empty function
        (checkDataTypeNumericMoney(element.fieldDataType) && !functionName);
    }

    let config = resolveAvailableFunction(this.chartType);

    config = config && config[container.name];

    if (!config || !result) {
      return result;
    }

    return ReportPartUtils.checkCompatibleFunctionCommon(functionInfo, element, config);
  }

  /**
   * validate function based on container
   * @param {Object} container
   * @param {Object} functionInfo
   * @returns {Object}
   */
  validateFunction(container: any, functionInfo: any): any {
    return !get('name', functionInfo)
      ? {
          status: true,
          useDefaultFunction: true
        }
      : {
          status: true
        };
  }

  /**
   * Get Default Function And Format for field in container
   * @param {Object} container
   * @param {string} dataType
   * @returns {Object}
   */
  getDefaultFunctionFormat(container: any, dataType: string): any {
    if (
      container.name === 'values' &&
      this.defaultOptionsByType.hasOwnProperty('izRange') &&
      !!get('optionByType.izRange', this.properties)
    ) {
      return null;
    }

    const baseChartType = this.getBaseChartType(this.chartType) || '';
    const defaultFunction =
      get(this.chartType, CONSTANTS.DefaultFunction) || get(baseChartType, CONSTANTS.DefaultFunction);

    const byContainer = (defaultFunction && defaultFunction[container.name || '']) || {};

    return ReportPartUtils.getDefaultFunction(byContainer, dataType);
  }

  /**
   * Mark dirty when user touch on Multi Color option
   */
  setMultiColorDirty(): void {
    this.multiColorDirty = true;
  }

  /**
   * set total label
   * @param {Object} value
   * TODO: move to report part chart content
   */
  @action('setTotalLabel')
  setTotalLabel(value: any): void {
    this.properties.optionByType.izTotalLabel = value;
    this.markDirty();
  }

  /**
   * action invoke after insert new field into container
   * @param {Object} container
   * @param {Object} field
   */
  @action('afterInsertFieldToContainer')
  afterInsertFieldToContainer(container: any, field: any): void {
    const metric = this.getMetricContainer();
    // set metric to the last inserted field in metric container
    container.name === metric && this.setActiveSerie(null, last(this[metric].elements));
    this.updateMultiColorPropValue();

    switch (true) {
      case this.isChartType(this.chartType, CHART_TYPE.Combination):
        this.initCombinationElementData(field);
        break;
      case this.isChartType(this.chartType, CHART_TYPE.Sparkline):
        this.initSparklineElementData(field);
        break;
    }
  }

  /**
   * After add field
   */
  @action('afterAddField')
  afterAddField(): void {
    if (this.isBeingBuild) {
      // Reset sort value  to 'ASC' for fields in LABEL if it matches conditional linear type
      forEach(item => {
        ReportPartUtils.isLinear(item, this.defaultOptionsByType) && this.resetSortAscending(item);
      }, this.labelsElements);

      // Set sort value 'ASC' for first field in LABEL if there isn't any existing sorted fields
      const firstPosition = 1;
      const lengthToApplySort = 2;
      const { ASC, DESC } = SORT_ORDERS;
      const needSortFirstField =
        this.labelsElements.length >= lengthToApplySort &&
        !some(field => {
          const sort = get('properties.dataFormattings.sort', field);
          return includes(sort, [ASC.value, DESC.value]);
        }, this.allFieldCollection);
      const firstField = find(ele => ele.position === firstPosition, this.labelsElements);
      needSortFirstField && firstField.properties.setSortValue(ASC.value);
    }
  }

  /**
   * update value of izDisableMultiColor
   * @method updateMultiColorPropValue
   */
  updateMultiColorPropValue() {
    this.properties.commonOptions.izDisableMultiColor = this.isDisableMultiColor;
  }

  /**
   * This function is to show the confirm message to disable multi color in case of there are more than one metric.
   * @method validateMoreThanOneMetric
   * @param {Object} container: container to add more metric
   */
  validateMoreThanOneMetric(container: any): any {
    const type = get('properties.chartType', this);
    const metricContainer = this.getMetricContainer();
    const numberOfMetrics = get('numberOfMetric', this);
    const izMultiColor = get('properties.optionByType.izMultiColor', this);
    const isDisableMultiColor = get('properties.commonOptions.izDisableMultiColor', this);
    // Sparkline and HeatMap DO NOT support multi color so we dont need to show error message
    if (
      isDisableMultiColor === false &&
      !this.isChartType(
        type,
        CHART_TYPE.Sparkline,
        CHART_TYPE.HeatMap,
        CHART_TYPE.Waterfall,
        CHART_TYPE.Donut,
        CHART_TYPE.Pie,
        CHART_TYPE.Funnel,
        CHART_TYPE.TreeMap
      ) &&
      metricContainer === container.name &&
      izMultiColor === true &&
      numberOfMetrics > NUMBER_ZERO
    ) {
      return ReportBodyConstants.MESSAGES.M29;
    }
    return null;
  }

  /**
   * Check if report part content had setting regression line
   */
  hadSettingRegressionLine() {
    return some(ele => !isEmpty(get('regression', ele.properties.getOtherProps())), this.serieChartList);
  }

  /**
   * Validate remove regression line when add separator
   */
  validateRemoveRegressionLine(container: string) {
    return container.name === ContainerName.SEPARATORS && this.hadSettingRegressionLine()
      ? this.isChartType(
          this.chartType,
          CHART_TYPE.Sparkline,
          CHART_TYPE.HeatMap,
          CHART_TYPE.Waterfall,
          CHART_TYPE.Donut,
          CHART_TYPE.Pie,
          CHART_TYPE.Funnel,
          CHART_TYPE.TreeMap
        )
        ? { remove: true, message: null }
        : { remove: true, message: ReportBodyConstants.MESSAGES.M30 }
      : { remove: false, message: null };
  }

  /**
   * after update function for field
   * @method afterUpdateFunction
   */
  @action('afterUpdateFunction')
  afterUpdateFunction(): void {
    this.inconsitentFunction =
      some({ haveFunction: true }, this.allFieldCollection) &&
      some(item => !item.haveFunction && !item.field.isRunningField, this.allFieldCollection);
  }

  /**
   * Action is invoked after removing an field from container
   * @param {Object} container
   */
  @action('afterRemoveField')
  afterRemoveField(container: any): void {
    // Update properties yAxis option along with deleted element
    if (container.name === this.getMetricContainer()) {
      this.updateMultiColorPropValue();
      container.elements.length === NUMBER_ONE &&
        this.properties.commonXYAxis &&
        (this.properties.commonXYAxis.metricAllOption = {});
      container.elements.length >= NUMBER_ONE && this.setActiveSerie(null, head(container.elements));
    }
  }

  /**
   * Init field data for Combination chart
   * @param {Object} element
   * @param {boolean} isResetData
   */
  initCombinationElementData(element: any, isResetData: boolean): void {
    const elementProps = element && element.properties;
    let otherProps = elementProps && elementProps.getOtherProps && elementProps.getOtherProps();
    otherProps = merge(otherProps, {
      yAxis: isResetData ? undefined : COMBINE_CHART_DEFAULT_VALUE.yAxis,
      chartType: isResetData ? undefined : COMBINE_CHART_DEFAULT_VALUE.chartType,
      lineWidth: isResetData ? undefined : COMBINE_CHART_DEFAULT_VALUE.lineWidth,
      dashStyle: isResetData ? undefined : COMBINE_CHART_DEFAULT_VALUE.dashStyle
    });
    elementProps && elementProps.setOtherProps && elementProps.setOtherProps(undefined, otherProps);
  }

  /**
   * Init field data for Sparkline chart
   * @param {Object} element
   * @param {boolean} isResetData
   */
  initSparklineElementData(element: any, isResetData: boolean): void {
    const elementProps = element && element.properties;
    let otherProps = elementProps && elementProps.getOtherProps && elementProps.getOtherProps();
    otherProps = merge(otherProps, {
      chartType: isResetData ? undefined : SPARKLINE_CHART_DEFAULT_VALUE.chartType,
      lineWidth: isResetData ? undefined : SPARKLINE_CHART_DEFAULT_VALUE.lineWidth,
      dashStyle: isResetData ? undefined : SPARKLINE_CHART_DEFAULT_VALUE.dashStyle,
      showTotal: false
    });
    elementProps.setOtherProps && elementProps.setOtherProps(undefined, otherProps);
  }

  /**
   * read data from api/create report part to update data
   * @param {Object} content [data from api/create report part]
   */
  @action('readData')
  readData(content: any): void {
    content = content || {};
    const properties = content.properties || {};
    this.isCrossFiltering = content.isCrossFiltering;
    // report part properties
    this.staticProperties = get('staticProperties', properties);
    this.setReportPartProperties(omit('staticProperties', properties));
    // this.type = content.type;
    this.labels = populateReportChartContainer(content.labels, 'labels');
    this.values = populateReportChartContainer(content.values, 'values');
    populateReportChartCustomContainer(content.customContainers, this.customContainers);
    this.valuesLabels = populateReportChartContainer(content.valuesLabels, 'valuesLabels');
    this.separators = populateReportChartContainer(content.separators, 'separators');
    this.bubbleSize = populateReportChartContainer(content.bubbleSize, 'bubbleSize');
    this.settings = content.settings;
    this.setActiveSerie(null, find({ fieldName: content.selectedMetric }, this.serieChartList));
    this.updateMultiColorPropValue();
    this.containerHistoryStore(this.chartType);

  }

  /**
   * Set data by data key
   * @param {string} key
   * @param {Object} newVal
   */
  setDataByKey(key: string, newVal: any): void {
    this[key] = newVal;
    this.markDirty();
  }

  /**
   * Get data by data key
   * @param {string} key
   * @return {Object}
   */
  getDataByKey(key: string): any {
    return this[key];
  }

  /**
   * get report part properties
   * @param {string} propKey
   * @return {Object}
   */
  getReportPartProperties(propKey: string): any {
    const props = this.properties || {};

    if (!propKey) {
      return assign({ staticProperties: this.staticProperties }, props);
    }

    let definedVal = getOr(get(propKey, props.optionByType || {}), propKey, props.commonOptions || {});
    definedVal = isUndefined(definedVal) ? get(propKey, props.view) : definedVal;
    definedVal = isUndefined(definedVal) ? get(propKey, props.commonXYAxis) : definedVal;
    definedVal = isUndefined(definedVal) ? get(propKey, props.printing) : definedVal;

    if (isUndefined(definedVal)) {
      return getOr(get(propKey, this.defaultOptionsByType || {}), propKey, this.defaultCommonOptions);
    }
    return definedVal;
  }

  /**
   * set report part properties
   * @param {Object} newProps
   */
  @action('setReportPartProperties')
  setReportPartProperties(newProps: any): void {
    const updateVal = (key: string, defaultValue: any) => {
      let source = newProps[key] || defaultValue;

      if (isArray(source) || isObservableArray(source)) {
        props[key].replace(source);
      } else if (isObject(source) || isObservable(source)) {
        source = omitBy(isUndefined, source);
        extendObservable(props[key], toJS(source));
      }
    };

    newProps = newProps || {};
    const props = this.properties;

    // The chart type must be updated firstly as the subsequential properties resolves upon chart type
    newProps['chartType'] && (props['chartType'] = newProps['chartType']);
    newProps['vizEngine'] && (props['vizEngine'] = newProps['vizEngine']);

    updateVal('commonOptions', this.defaultCommonOptions);
    updateVal('optionByType', this.defaultOptionsByType);
    updateVal('commonXYAxis', {});
    updateVal('view', {});
    updateVal('printing', {});

    if (!isUndefined(newProps.colorTheme) || newProps.isDefaultTheme) {
      props.isDefaultTheme = newProps.isDefaultTheme;
      props.colorTheme = newProps.colorTheme;
    }
    this.markDirty();
  }

  /**
   * build data for save
   * @returns {Object}
   */
  @action('buildDataForSave')
  buildDataForSave(): any {
    const chartContent = omit(['properties', 'staticProperties', 'expandedLevel', 'drilldownInfo'], this);

    // merge static properties
    chartContent.properties = this.getReportPartProperties();
    chartContent.selectedMetric = get('activeSerie.fieldName', this);

    return chartContent;
  }

  /**
   * Remove dynamic threshold for field which were set invisible
   */
  @action
  removeInvisibleDynamicThreshold() {
    const elements = flatMap(container => container.elements.slice(), this.containerCollection);
    const thresholdElements = get('elements', find({ name: ContainerName.DYNAMIC_THRESHOLD }, this.customContainers));
    const commonXYAxis = get('properties.commonXYAxis', this);
    if (thresholdElements) {
      if (commonXYAxis && !isEmpty(commonXYAxis.allDynamicThresholdLine)) {
        commonXYAxis.allDynamicThresholdLine = remove(
          setting => !find(ele => ele.fieldNameAlias === setting.fieldName, thresholdElements),
          commonXYAxis.allDynamicThresholdLine
        );
      }

      if (!isEmpty(elements)) {
        elements.forEach(ele => {
          const otherProps = ele.properties.getOtherProps();
          if (!isEmpty(otherProps.dynamicThresholds)) {
            otherProps.dynamicThresholds = remove(
              setting => !find(ele => ele.fieldNameAlias === setting.fieldName, thresholdElements),
              otherProps.dynamicThresholds
            );

            // Remove completely if no setting remains
            if (isEmpty(otherProps.dynamicThresholds)) {
              resetDefaultSetting([ele], 'dynamicThresholds');
            }
          }
        });
      }
    }
  }

  /**
   * Remove all unuse threshold
   */
  @action
  removeUnuseDynamicThresholds() {
    this.removeInvisibleDynamicThreshold();

    // Support all type of threshold
    if (this.isChartType(this.chartType, CHART_TYPE.Line, CHART_TYPE.Column, CHART_TYPE.Bar, CHART_TYPE.Area)) {
      return;
    }

    // Do not support threshold
    const removeAllSetting = this.isChartType(
      this.chartType,
      CHART_TYPE.Pie,
      CHART_TYPE.Donut,
      CHART_TYPE.Funnel,
      CHART_TYPE.TreeMap,
      CHART_TYPE.HeatMap,
      CHART_TYPE.Sparkline
    );

    // Do not support dynamic threshold
    const removeDynamic = this.isChartType(this.chartType, CHART_TYPE.Bubble, CHART_TYPE.Scatter);

    // Do not support all metric
    const removeAllMetric = this.isChartType(
      this.chartType,
      CHART_TYPE.Combination,
      CHART_TYPE.Waterfall,
      CHART_TYPE.Bubble,
      CHART_TYPE.Scatter
    );
    this.resetThresholdDefaultValue(removeAllSetting, removeDynamic, removeAllMetric);
  }

  /**
   * Get dirty status.
   * @returns {boolean}
   */
  checkDirty(): boolean {
    var dirtyElements = ReportUtils.dirtyReportPartElementField(this.containerCollection);
    var checkDirty = get('checkDirty', this.properties);

    return this.isDirty || !isEmpty(dirtyElements) || (isFunction(checkDirty) && checkDirty());
  }

  /**
   * Clean dirty status.
   */
  cleanDirty(): void {
    this.resetDirty();
  }

  /**
   * Set Chart type
   * @param {string} nextType
   * @returns {boolean}
   */
  @action
  setChartType(nextType: string): boolean {
    const previousType = this.properties.chartType;
    this.properties.chartType = nextType;
    this.setVizEngineByCurrentChartType();
    this.setDefaultOptionsByTypeByCurrentChartType();
    this.isLinear() && this.resetSortAscending(this.currentDrilldownField);
    this.markDirty();

    return this.convertChartType(previousType, nextType);
  }

  /**
   * Set value for Multi Color option
   * @param {boolean} nextValue
   */
  setMultiColor(nextValue: boolean): void {
    const optionByType = get('properties.optionByType', this);
    if (has('izMultiColor', optionByType)) {
      optionByType.izMultiColor = nextValue;
    }
  }

  isLineLikeChart = chartType =>
    this.isChartType(chartType, CHART_TYPE.Line, CHART_TYPE.Pie, CHART_TYPE.Funnel, CHART_TYPE.Donut,
      CHART_TYPE.TreeMap, CHART_TYPE.Column, CHART_TYPE.Bar, CHART_TYPE.Area, CHART_TYPE.Combination);

  isBubbleLikeChart = chartType =>
    this.isChartType(chartType, CHART_TYPE.Bubble, CHART_TYPE.Scatter);

  /**
   * Whether we need to reset metric when switching chart type or not.
   * @param {string} previousType previous chart type.
   * @param {string} nextType next chart type.
   * @returns {boolean}
   */
  metricIsOkay(previousType: string, nextType: string): boolean {
    return (this.isLineLikeChart(previousType) && this.isLineLikeChart(nextType))
      || (this.isBubbleLikeChart(previousType) && this.isBubbleLikeChart(nextType));
  }

  /**
   * Convert chart type
   * @param {string} previousType
   * @param {string} nextType
   * @return {boolean} TRUE need to update preview data, otherwise FALSE
   */
  @action('convertChartType')
  convertChartType(previousType: string, nextType: string): boolean {
    let needReloadData = false;

    // Convert container elements:
    // First we're converting elements history into default containers configuration:
    // [labels: many, values: many, separators: many]
    this.containerHistoryStore(previousType);
    // Then we restore from default containers configuration into the required chart type
    // Also, here we're updating elements positions.
    this.containerHistoryRestore(nextType);

    // Prepare previous type:
    switch (true) {
      case this.isChartType(previousType, CHART_TYPE.Column, CHART_TYPE.Bar, CHART_TYPE.Area):
        if (!isNil(this.properties.optionByType.izRange)) {
          needReloadData = true;
          this.properties.optionByType.izRange = '';
        }
        break;
      case this.isChartType(previousType, CHART_TYPE.Combination):
        this.initCombinationChartData(true);
        break;
      case this.isChartType(previousType, CHART_TYPE.Sparkline):
        this.initSparklineChartData(true);
        break;
    }
    // Prepare next type:
    switch (true) {
      case this.isChartType(nextType, CHART_TYPE.Combination):
        this.initCombinationChartData();
        break;
      case this.isChartType(nextType, CHART_TYPE.Sparkline):
        this.initSparklineChartData();
        break;
    }

    if (this.separators.elements.length) {
      this.properties.optionByType = this.properties.optionByType || {};
      this.properties.optionByType.izUseSeparator = true;
    }

    // Reset metric only if it is incompatible.
    const historyMetric = this.getHistoryMetric(nextType);
    if (!historyMetric.isNew) {
      this.setActiveSerie('', historyMetric.serieInfo);
    } else {
      // if metric cache for nextType weren't set previously: try to use metric from
      // previous type, otherwise reset it.
      if (!this.metricIsOkay(previousType, nextType)) {
        this.resetMetric(nextType);
      }
    }
    if (!this.metricIsOkay(previousType, nextType)) {
      needReloadData = true;
    }

    this.markDirty();
    this.updateMultiColorPropValue();

    // whether to update data if chart-type is changed
    if (
      this.isChartType(previousType, ...NEED_UPDATE_DATA_CHART_TYPE) ||
      this.isChartType(nextType, ...NEED_UPDATE_DATA_CHART_TYPE)
    ) {
      this.resetDrilldownInfo();
      needReloadData = true;
    }

    // check function of each element >> reset to default in case invalid
    const promiseArray = flow(
      flatMap(container =>
        map(element => {
          const needUpdateFunction = !this.checkedCompatibleFunction(
            container,
            element,
            getOr({}, 'properties.dataFormattings.functionInfo', element)
          );

          // update function if the current function not compatible
          if (needUpdateFunction) {
            return ReportPartUtils.setDefaultFunctionElement(this, container, element).then(() => needReloadData);
          }

          return needReloadData;
        }, container.elements)
      ),
      compact
    )(this.containerCollection);

    return Promise.all(promiseArray);
  }

  /**
   * Init combination chart data
   * set other props for values element
   * @param {boolean} isResetData
   */
  @action('initCombinationChartData')
  initCombinationChartData(isResetData: boolean): void {
    this.values.elements.forEach(element => {
      this.initCombinationElementData(element, isResetData);
    });
  }

  /**
   * Init Sparkline chart data
   * set other props for values element
   * @param {boolean} isResetData
   */
  @action('initSparklineChartData')
  initSparklineChartData(isResetData: boolean): void {
    this.values.elements.forEach(element => {
      this.initSparklineElementData(element, isResetData);
    });
  }

  /**
   * Merge container elements
   * @returns {Array<any>}
   */
  @action('mergeContainerElements')
  mergeContainerElements(): Array<any> {
    const re = [];
    let i, container;

    for (i = NUMBER_ZERO; i < arguments.length; i++) {
      container = this[arguments[i]];

      if (container && !isEmpty(container.elements)) {
        // merge to result-array
        container.elements.forEach(elm => {
          elm.position = re.length + NUMBER_ONE;
          re.push(elm);
        });

        // reset container
        container.elements = [];
      }
    }

    return re;
  }

  /**
   * Update element position
   * This is used for merging elements of many container into one
   */
  @action('updateContainerElementsPosition')
  updateContainerElementsPosition(...containers): void {
    for (let i = 0; i < containers.length; i++) {
      containers[i].elements.map((ele, index) => {
        ele.position = index + NUMBER_ONE;
      });
    }
  }

  /**
   * Remove undefined
   * @param {Array<any>} array
   * @returns {Object}
   */
  @action('removeUndefined')
  removeUndefined(array: Array<any>): any {
    return remove(isUndefined, array);
  }

  /**
   * Reset container elements
   */
  @action('resetContainer')
  resetContainer(...containers): void {
    forEach(c => {
      c.elements = [];
    }, containers);
  }

  /**
   * Reset separator elements
   * @param {boolean} showSeparator
   */
  @action('resetSeparator')
  resetSeparator(showSeparator: boolean): void {
    !showSeparator && this.separators.elements.clear();
  }

  /**
   * Get the first values elements as default metrics options
   * @returns {Object}
   */
  getActiveSerieKey(): any {
    const defaultValue = get('fieldName', first(this.getMetricElements()));
    return isNil(this.activeSerie) ? defaultValue : this.activeSerie.name;
  }

  /**
   * Get metric elements
   */
  getMetricElements() {
    return get(`${this.getMetricContainer()}.visibleElement`, this);
  }

  /**
   * Get active elements by active series
   * @param {string} activeSerieKey
   * @return {Object}
   */
  getActiveElmsByActiveSeries = (activeSerieKey: string): any => {
    const metricContainer = this.getMetricContainer();
    const getActiveElement = container => getElements(container, activeSerieKey, metricContainer);

    const customContainersObject = reduce(
      (obj, container) => ({ ...obj, [container.name]: getActiveElement(container) }),
      {}
    )(this.customContainers);

    return {
      labels: getActiveElement(this.labels),
      values: getActiveElement(this.values),
      valuesLabels: getActiveElement(this.valuesLabels),
      separators: getActiveElement(this.separators),
      bubbleSize: getActiveElement(this.bubbleSize),
      ...customContainersObject
    };
  };

  /**
   * Get container which will be shown in metric list
   * @returns {string}
   */
  getMetricContainer(): string {
    return this.isChartType(this.chartType, CHART_TYPE.HeatMap, CHART_TYPE.Bubble, CHART_TYPE.Scatter)
      ? 'valuesLabels'
      : 'values';
  }

  /**
   * Get container which will be used for drilldown
   * @returns {string}
   */
  getDrildownContainer(): string {
    // always use labels to drilldown for all chart-type
    return 'labels';
  }

  /**
   * Reset metric after changed chart type;
   */
  resetMetric(nextType): void {
    const metric = this.getMetricContainer();
    const defaultMetric = first(this[metric].elements);
    this.setActiveSerie(null, defaultMetric);
    // remember metric
    const historyMetric = this.getHistoryMetric(nextType);
    historyMetric.serieInfo = defaultMetric;
  }

  /**
   * Set active serie key, used to show/hide serie chart in metric dropdown
   * @param {Object} activeSerieKey
   * @param {Object} activeSerieInfo
   */
  setActiveSerie(activeSerieKey: any, activeSerieInfo: any): void {
    this.activeSerie = activeSerieInfo;
    // remember metric
    const historyMetric = this.getHistoryMetric(this.chartType);
    historyMetric.serieInfo = activeSerieInfo;
    this.markDirty();
  }

  /**
   * Reset drilldown info whenever reorder field
   */
  @action('changePositionField')
  changePositionField(): void {
    this.resetDrilldownInfo();
  }

  /**
   * Update drilldown inform
   * @param {Object} drilldownValue
   * @param {Object} separatorValue
   * @returns {boolean}
   */
  @action('updateDrilldownInfo')
  updateDrilldownInfo(drilldownValue: any, separatorValue: any): boolean {
    if (!this.hasDrillDown) {
      return false;
    }

    const filters = get('filters', this.drilldownInfo);
    let canDrilldown = false;

    // move to top level
    if (!drilldownValue && size(filters)) {
      this.resetDrilldownInfo();
      canDrilldown = true;
      return canDrilldown;
    }

    const drilldownField = get('field', drilldownValue);
    const nextDrilldownKey = get('fieldNameAlias', drilldownField) || drilldownValue.key;
    const drillUpLevel = findIndex({ key: nextDrilldownKey }, filters);
    const field = get('field', drilldownValue);

    if (drillUpLevel > NUMBER_CHECK_INDEX) {
      // drill up
      filters.splice(drillUpLevel + NUMBER_ONE, filters.length - drillUpLevel - NUMBER_ONE);
      canDrilldown = true;
    } else if (filters.length < this[this.getDrildownContainer()].numElement - NUMBER_ONE) {
      // drill down
      canDrilldown = true;

      filters.push({
        key: nextDrilldownKey,
        value: ReportPartUtils.getOriginalValue(field, drilldownValue.record),
        field: drilldownField,
        record: get('record', drilldownValue)
      });

      separatorValue &&
        separatorValue.field &&
        (this.drilldownInfo.separatorValue = {
          key: get('field.fieldNameAlias', separatorValue),
          value: get('value', separatorValue),
          field: get('field', separatorValue)
        });
    }

    return canDrilldown;
  }

  /**
   * Can drill down from given level
   * @param {Number} currentPosition
   */
  canDrillDown(currentPosition: Number): boolean {
    if (!this.hasDrillDown) {
      return false;
    }

    return size(this[this.getDrildownContainer()].visibleElement) > currentPosition;
  }

  /**
   * Reset drilldown info
   */
  @action('resetDrilldownInfo')
  resetDrilldownInfo = (): void => {
    this.drilldownInfo = {
      filters: [],
      separatorValue: null
    };
  };

  /**
   * Return drilldown info: expandedLevel & filters
   * @return {Object}
   */
  getDrilldownInfo(): any {
    let filters = this.drilldownInfo.filters.map(filterItem => {
      return {
        key: get('key', filterItem),
        value: get('value', filterItem),
        dataType: get('field.reportPartElm.fieldDataType', filterItem)
      };
    });

    const separatorInfo = {
      key: get('key', this.drilldownInfo.separatorValue),
      value: get('value', this.drilldownInfo.separatorValue),
      isSeparator: true
    };

    filters =
      this.expandedLevel < NUMBER_ZERO
        ? []
        : this.drilldownInfo.separatorValue
        ? filters.unshift(separatorInfo) && filters
        : filters;

    return {
      expandedLevel: this.expandedLevel,
      filters: filters
    };
  }

  /**
   * Return Cross Filtering info.
   * @returns {Array<any>}
   */
  getCrossFilteringInfo(): Array<any> {
    return this.drilldownInfo.filters.map(filterItem => {
      return {
        key: get('key', filterItem),
        value: get('value', filterItem),
        field: get('field', filterItem)
      };
    });
  }

  /**
   * Get xaxis-title options for xyplane and threshold
   * This options are retrieved base on field in label container dataType
   * @param {boolean} dataTypeCheck
   * @returns {Array<any>}
   */
  getXAxisTitleOption(dataTypeCheck: boolean): Array<any> {
    const list = dataTypeCheck ? this.listLinearFieldInLabelContainer : this.labelsElements;
    const options = list.map(ele => {
      return {
        value: ele.fieldName,
        label: ele.fieldName,
        dateTimeFormatData: ele.dateTimeFormatData
      };
    });
    return options || [];
  }

  /**
   * Get yaxis-title options for xyplane and threshold
   * This options are retrieved base on field in label container dataType
   * @param {boolean} dataTypeCheck
   * @returns {Array<any>}
   */
  getYAxisTitleOption(dataTypeCheck: boolean, useForThreshold: boolean): Array<any> {
    const list = dataTypeCheck ? this.listCountableFieldInValueContainer : this.valuesElements;
    const options = [];
    if (
      (list.length > NUMBER_ONE || useForThreshold) &&
      !this.isChartType(
        this.chartType,
        CHART_TYPE.Combination,
        CHART_TYPE.Sparkline,
        CHART_TYPE.Bubble,
        CHART_TYPE.Waterfall,
        CHART_TYPE.Scatter
      )
    ) {
      options.push({ value: '', label: METRIC_ALL });
    }
    const eleOptions = list
    .filter(ele => useForThreshold || isEmpty(get('yAxis', ele.properties.getOtherProps())))
    .map(ele => {
      return {
        value: ele.fieldName,
        label: ele.fieldName,
        dateTimeFormatData: ele.dateTimeFormatData
      };
    });
    return options.concat(eleOptions);
  }

  /**
   * This properties is used to save the page size
   * Only use for chart-type which supported pagination: pie, donut, treemap, funnel
   * @param {Object} pageSize
   */
  setPageSize(pageSize: any): void {
    this.staticProperties.pageSize = pageSize;
    this.markDirty();
  }

  /**
   * Check need to break page when printing.
   * @returns {boolean}
   */
  checkBreakPage(): boolean {
    return (
      this.properties.printing.izPageBreakAfterSeparator &&
      this.separators.elements &&
      this.separators.elements.length > NUMBER_ZERO
    );
  }

  /**
   * Action whenever element's props are changed.
   * @param {Object} element
   * @param {Object} elementProps
   * @param {string} changedKey
   */
  changePropertiesByField(element: any, elementProps: any, changedKey: string) {
    if (changedKey === 'function' || changedKey === 'format') {
      this.resetDrilldownInfo();
    }
  }

  /**
   * Check if this field can have state Unsort
   * @param {Object} container
   * @returns {boolean}
   */
  checkCanUnsort(container: any): boolean {
    const containerName = getOr('', 'name', container);
    let canUnsort = false;

    switch (true) {
      case this.isChartType(
        this.chartType,
        CHART_TYPE.Line,
        CHART_TYPE.Column,
        CHART_TYPE.Bar,
        CHART_TYPE.Area,
        CHART_TYPE.Pie,
        CHART_TYPE.Funnel,
        CHART_TYPE.Donut,
        CHART_TYPE.Sparkline
      ):
        canUnsort =
          containerName !== ContainerName.SEPARATORS ||
          this.isChartType(this.chartType, CHART_TYPE.Area, CHART_TYPE.Bar, CHART_TYPE.Column, CHART_TYPE.Line);
        break;
      case this.isChartType(this.chartType, CHART_TYPE.Combination, CHART_TYPE.Waterfall):
        canUnsort = true;
        break;
      case this.isChartType(this.chartType, CHART_TYPE.HeatMap, CHART_TYPE.Scatter):
        canUnsort = containerName !== ContainerName.VALUES_LABELS;
        break;
      case this.isChartType(this.chartType, CHART_TYPE.Bubble):
        canUnsort = containerName !== ContainerName.VALUES_LABELS && containerName !== ContainerName.BUBBLE_SIZE;
        break;
    }

    let canUnsortForLinear = true;
    if (containerName === ContainerName.LABELS) {
      canUnsortForLinear = !ReportPartUtils.isLinear(this.selectedField, this.defaultOptionsByType);
    } else if (containerName === ContainerName.VALUES) {
      canUnsortForLinear = !this.isLinear();
    }

    return this.isBeingBuild ? canUnsort && canUnsortForLinear : canUnsort;
  }

  /**
   * Check if this field hidden
   * @param {Object} field
   * @param {Object} container
   * @returns {boolean}
   */
  checkHiddenSortField(field: any, container: any): boolean {
    const containerName = getOr('', 'name', container);

    switch (true) {
      case this.isChartType(this.chartType, CHART_TYPE.Scatter, CHART_TYPE.HeatMap):
        return containerName === ContainerName.VALUES_LABELS;
      case this.isChartType(this.chartType, CHART_TYPE.TreeMap):
        return containerName !== ContainerName.SEPARATORS;
      case this.isChartType(this.chartType, CHART_TYPE.Bubble):
        return containerName === ContainerName.VALUES_LABELS || containerName === ContainerName.BUBBLE_SIZE;
    }

    return false;
  }

  /**
   * Reset grid line setting to default value
   */
  @action('resetGridLineDefaultValue')
  resetGridLineDefaultValue(): void {
    this.properties.commonXYAxis.izGridLine = {};
    this.markDirty();
  }

  /**
   * Reset border setting to default value
   */
  @action('resetBorderDefaultValue')
  resetBorderDefaultValue(): void {
    this.properties.commonOptions['izEntire.borderColor'] =
      CONSTANTS.DEFAULT_SETTING_VALUE.borderDefault['izEntire.borderColor'];
    this.properties.commonOptions['izEntire.borderWidth'] =
      CONSTANTS.DEFAULT_SETTING_VALUE.borderDefault['izEntire.borderWidth'];
    this.properties.commonOptions['izEntire.borderStyle'] =
      CONSTANTS.DEFAULT_SETTING_VALUE.borderDefault['izEntire.borderStyle'];
    this.markDirty();
  }

  /**
   * Reset background color setting to default value
   */
  @action('resetBackgroundColorDefaultValue')
  resetBackgroundColorDefaultValue(): void {
    this.properties.commonOptions['izEntire.backgroundColor'] =
      CONSTANTS.DEFAULT_SETTING_VALUE.backGroundColorDefault['izEntire.backgroundColor'];
    this.properties.commonOptions['izPlot.backgroundColor'] =
      CONSTANTS.DEFAULT_SETTING_VALUE.backGroundColorDefault['izPlot.backgroundColor'];
    this.markDirty();
  }

  /**
   * Reset XYPlane default setting to default value
   */
  @action('resetXYPlaneDefaultValue')
  resetXYPlaneDefaultValue(): void {
    resetDefaultSetting(this.labelsElements, 'axis');
    resetDefaultSetting(this.valuesElements, 'axis');
    this.markDirty();
  }

  /**
   * Reset threshold setting to default value
   */
  @action('resetThresholdDefaultValue')
  resetThresholdDefaultValue(
    removeAllSetting: boolean = true,
    removeDynamic: boolean = true,
    removeAllMetric: boolean = true
  ): void {
    if (removeAllSetting) {
      resetDefaultSetting(this.labelsElements, 'threshold');
      resetDefaultSetting(this.valuesElements, 'threshold');
      resetDefaultSetting(this.valuesLabels.elements, 'threshold');
      resetDefaultSetting(this.bubbleSize.elements, 'threshold');
    }

    if (removeAllSetting || removeDynamic) {
      resetDefaultSetting(this.labelsElements, 'dynamicThresholds');
      resetDefaultSetting(this.valuesElements, 'dynamicThresholds');
      resetDefaultSetting(this.valuesLabels.elements, 'dynamicThresholds');
      resetDefaultSetting(this.bubbleSize.elements, 'dynamicThresholds');
      const dynamicThresholdsContainer = find({ name: ContainerName.DYNAMIC_THRESHOLD }, this.customContainers);
      dynamicThresholdsContainer.elements.clear();
    }

    const commonXYAxis = get('properties.commonXYAxis', this);
    if (commonXYAxis && (removeAllSetting || removeAllMetric)) {
      commonXYAxis.allDynamicThresholdLine = [];
      commonXYAxis.metricAllPlotLinesOption = [];
    }

    this.markDirty();
  }

  /**
   * Reset regression line setting to default value
   */
  @action
  resetRegressionLineDefaultValue(): void {
    resetDefaultSetting(this.serieChartList, 'regression');

    this.markDirty();
  }

  /**
   * Reset refresh interval setting to default vale
   */
  @action('resetRefreshIntervalValue')
  resetRefreshIntervalValue(): void {
    this.properties.view.dataRefreshInterval = CONSTANTS.DEFAULT_SETTING_VALUE.refreshIntervalDefault;
    this.markDirty();
  }

  /**
   * Reset legend setting to default
   */
  @action('resetLegendDefaultValue')
  resetLegendDefaultValue(): void {
    this.properties.commonOptions['izLegend.visibility'] = false;
    this.markDirty();
  }

  /**
   * Check if ReportPart is drilling down or not
   * @returns {boolean}
   */
  isDrillingDown(): boolean {
    return !flow(
      get('drilldownInfo.filters'),
      isEmpty
    )(this);
  }

  /**
   * Check if xAxis is linear type
   * @returns {boolean}
   */
  isLinear(): boolean {
    return this.isBeingBuild ? ReportPartUtils.isLinear(this.currentDrilldownField, this.defaultOptionsByType) : false;
  }

  /**
   * Set configField
   * @param {Object} configField
   */
  setConfigField(configField: any): void {
    this.isBeingBuild &&
      ReportPartUtils.isLinear(configField, this.defaultOptionsByType) &&
      includes(configField, this.labelsElements) &&
      this.resetSortAscending(configField);

    this.selectedField = configField;
  }

  /**
   * Reset sort value to 'ASC' of drilldown field
   * @param {Object} configField
   * @return {boolean}
   */
  resetSortAscending(configField: any): boolean {
    const { ASC, DESC } = SORT_ORDERS;
    const sort = get('properties.dataFormattings.sort', configField);
    if (!includes(sort, [ASC.value, DESC.value])) {
      configField.properties.setSortValue(ASC.value);
      return true;
    }
    return false;
  }

  /**
   * Set default tick interval for Datetime datatype
   */
  @action
  setDefaultIntervalForDatetime(): void {
    const izTickInterval = get('properties.otherProps.axis.izTickInterval', this.currentDrilldownField);
    this.currentDrilldownField &&
      this.currentDrilldownField.previewDataTypeByFunction === DATA_TYPES.DATE_TIME &&
      isNil(izTickInterval) &&
      this.currentDrilldownField.properties.setOtherProps('axis', { izTickInterval: 1 });
  }

  /**
   * Update list dynamic threshold in field container.
   * @param {Array} elements
   */
  @action
  updateDynamicThresholdFieldInContainer(elements: Array<any>): void {
    const dynamicThresholdsContainer = find({ name: ContainerName.DYNAMIC_THRESHOLD }, this.customContainers);
    dynamicThresholdsContainer.elements = filter(item => !isNil(item), elements);
  }

  getHistoryMetric(chartType) {
    let actualChartType = chartType;
    if (this.isLineLikeChart(chartType))
      actualChartType = CHART_TYPE.Line;
    else if (this.isBubbleLikeChart(chartType))
      actualChartType = CHART_TYPE.Bubble;

    let historyMetric = find({chartType: actualChartType}, this.containersHistoryMetric);
    if (isNil(historyMetric)) {
      historyMetric = {
        isNew: true,
        chartType: actualChartType,
        serieInfo: null
      };
      this.containersHistoryMetric.push(historyMetric);
    } else {
      historyMetric.isNew = false;
    }
    return historyMetric;
  }

  getHistoryContainer(name) {
    let historyContainer = find({name: name}, this.containersHistory);
    if (isNil(historyContainer)) {
      historyContainer = {
        name: name,
        elements: []
      };
      this.containersHistory.push(historyContainer);
    }
    return historyContainer;
  }

  /**
   * Memoize elements.
   */
  containerHistoryStore(chartType) {
    this.containersHistory = this.containerHistoryToBase(chartType);
  }

  /**
   * After Filed Name Alias changed
   */
  afterChangeFieldNameAlias(oldAlias, newAlias){
    const valueElements = getOr([], 'values.elements', this);
    forEach(element => {
      const yAxisName = get('properties.otherProps.yAxis', element);
      if(yAxisName === oldAlias){
        element.properties.otherProps.yAxis = newAlias;
      }
    }, valueElements);
  }

  /**
   * After remove field
   */
  afterRemoveField = function(container, deletedField) {
    const deletedFieldNameAlias = getOr('', 'field.fieldNameAlias', deletedField);
    const valueElements = getOr([], 'values.elements', this);
    forEach(element => {
      const yAxisName = get('properties.otherProps.yAxis', element);
      if(yAxisName === deletedFieldNameAlias){
        element.properties.otherProps.yAxis = '';
      }
    }, valueElements);
  };

  /**
   * Restore container history for chart type.
   */
  containerHistoryRestore(chartType: string) {
    this.containerHistoryFromBase(chartType);
  }

  /**
   * Remove all elements from containers
   */
  containerHistoryCleanCurrent() {
    const containerNames = [
      ContainerName.LABELS,
      ContainerName.VALUES,
      ContainerName.VALUES_LABELS,
      ContainerName.SEPARATORS,
      ContainerName.BUBBLE_SIZE,
    ];
    forEach(containerName => {
      const container = getOr(null, containerName, this);
      if (container)
        container.elements = [];
    }, containerNames);
  }

  /**
   * Convert chart container elements from basic state:
   * [labels: many, values: many, separators: many]
   * @param {string} nextType chart type
   */
  containerHistoryFromBase(nextType: string) {
    // Get base containers from history cache and create new array instance:
    const historyValues = this.getHistoryContainer(ContainerName.VALUES);
    const historyLabels = this.getHistoryContainer(ContainerName.LABELS);
    const historySeparators = this.getHistoryContainer(ContainerName.SEPARATORS);

    // Clean up
    const historyElementValues = historyValues.elements = compact(historyValues.elements);
    const historyElementLabels = historyLabels.elements = compact(historyLabels.elements);
    const historyElementSeparators = historySeparators.elements = compact(historySeparators.elements);

    // Remove elements from containers
    this.containerHistoryCleanCurrent();

    switch (true) {

      case this.isChartType(nextType, CHART_TYPE.Combination):
        this.labels.elements = historyElementLabels;
        this.values.elements = slice(NUMBER_ZERO, NUMBER_FIVE, historyElementValues);
        break;

      case this.isChartType(nextType, CHART_TYPE.HeatMap):
        this.labels.elements = [head(historyElementLabels)];
        this.values.elements = [head(historyElementValues)];
        this.valuesLabels.elements = [nth(NUMBER_ONE, historyElementValues)];
        break;

      case this.isChartType(nextType, CHART_TYPE.Bubble):
        this.valuesLabels.elements = historyElementLabels;
        this.labels.elements = [head(historyElementValues)];
        this.values.elements = [nth(NUMBER_ONE, historyElementValues)];
        this.bubbleSize.elements = [nth(NUMBER_TWO, historyElementValues)];
        break;

      case this.isChartType(nextType, CHART_TYPE.Scatter):
        this.valuesLabels.elements = historyElementLabels;
        this.labels.elements = [head(historyElementValues)];
        this.values.elements = [nth(NUMBER_ONE, historyElementValues)];
        break;

      case this.isChartType(nextType, CHART_TYPE.Waterfall):
        this.labels.elements = [head(historyElementLabels)];
        this.values.elements = [head(historyElementValues)];
        break;

      case this.isChartType(nextType, CHART_TYPE.Sparkline):
        this.labels.elements = [head(historyElementLabels)];
        this.values.elements = slice(NUMBER_ZERO, NUMBER_FIVE, historyElementValues);
        this.separators.elements = historyElementSeparators;
        break;

      default:
        this.labels.elements = historyElementLabels;
        this.values.elements = historyElementValues;
        this.separators.elements = historyElementSeparators;
    }

    // Clean up elements
    this.labels.elements = compact(this.labels.elements);
    this.values.elements = compact(this.values.elements);
    this.separators.elements = compact(this.separators.elements);
    this.valuesLabels.elements = compact(this.valuesLabels.elements);
    this.bubbleSize.elements = compact(this.bubbleSize.elements);
    this.updateContainerElementsPosition(
      this.labels,
      this.values,
      this.bubbleSize,
      this.valuesLabels,
      this.separators
    );
  }

  /**
   * Convert chart container elements to basic state: [labels: many, values: many, separators: many]
   * missing values will be restored from history cache.
   * @param {string} previousType chart type
   * @returns object with labels, values and separator elements.
   */
  containerHistoryToBase(previousType: string): any {
    const skip = (count: number, array: any[]): any[] =>
      slice(count, array.length, array);

    const historyMetric = this.getHistoryMetric(previousType);
    historyMetric.serieInfo = this.activeSerie;

    // Get base containers from history cache and create new array instance:
    const historyLabels = this.getHistoryContainer(ContainerName.LABELS);
    const historyValues = this.getHistoryContainer(ContainerName.VALUES);
    const historySeparators = this.getHistoryContainer(ContainerName.SEPARATORS);

    const historyElementValues = historyValues.elements;
    const historyElementLabels = historyLabels.elements;
    const historyElementSeparators = historySeparators.elements;

    let newLabels = [];
    let newValues = [];
    let newSeparators = [];

    switch (true) {
      case this.isChartType(previousType, CHART_TYPE.Combination):
        newLabels = this.labels.elements;
        newValues = [
          ...this.values.elements,
          ...skip(NUMBER_FIVE, historyElementValues)];
        newSeparators = historyElementSeparators;
        break;

      case this.isChartType(previousType, CHART_TYPE.HeatMap):
        newLabels = [
          ...this.labels.elements,
          ...skip(NUMBER_ONE, historyElementLabels)];
        newValues = [
          ...this.values.elements,
          ...this.valuesLabels.elements,
          ...skip(NUMBER_TWO, historyElementValues)];
        newSeparators = historyElementSeparators;
        break;

      case this.isChartType(previousType, CHART_TYPE.Bubble):
        newLabels = this.valuesLabels.elements;
        newValues = [
          ...this.labels.elements,
          ...this.values.elements,
          ...this.bubbleSize.elements,
          ...skip(NUMBER_THREE, historyElementValues)];
        newSeparators = historyElementSeparators;
        break;

      case this.isChartType(previousType, CHART_TYPE.Scatter):
        newLabels = this.valuesLabels.elements;
        newValues = [
          ...this.labels.elements,
          ...this.values.elements,
          ...skip(NUMBER_TWO, historyElementValues)
        ];
        newSeparators = historyElementSeparators;
        break;

      case this.isChartType(previousType, CHART_TYPE.Waterfall):
        newLabels = [
          ...this.labels.elements,
          ...skip(NUMBER_ONE, historyElementLabels)
        ];
        newValues = [
          ...this.values.elements,
          ...skip(NUMBER_ONE, historyElementValues)
        ];
        newSeparators = historyElementSeparators;
        break;

      case this.isChartType(previousType, CHART_TYPE.Sparkline):
        newLabels = [
          ...this.labels.elements,
          ...skip(NUMBER_ONE, historyElementLabels)
        ];
        newValues = [
          ...this.values.elements,
          ...skip(NUMBER_FIVE, historyElementValues)
        ];
        newSeparators = this.separators.elements;
        break;

      default:
        newLabels = this.labels.elements;
        newValues = this.values.elements;
        newSeparators = this.separators.elements;
    }

    return [{
      name: ContainerName.LABELS,
      elements: compact(newLabels)
    }, {
      name: ContainerName.VALUES,
      elements: compact(newValues)
    }, {
      name: ContainerName.SEPARATORS,
      elements: compact(newSeparators)
    }];
  }

  /** Set default option type crossponding selected chart type */
  setDefaultOptionsByTypeByCurrentChartType() {
    this.properties['optionByType'] = this.defaultOptionsByType;
   }
}

export default ReportPartChartContent;



// WEBPACK FOOTER //
// ./ui/logic/models/Izenda.ReportPartChartContent.js